import httpClient from '@utils/httpClient';
import {isServer} from '@utils/isClient';
import {PROXY_PREFIXES, type ServiceName} from './constants';
import type {ApiCallOptions, ApiResponse, PaginatedResponse} from './types';
import {s2sTokenManager} from '../server/utils/s2sTokenManager';
import {getIsS2SEndpointAllowed, getS2SEndpointKey, S2S_AUTH_ENDPOINTS} from './s2s/config';
import getConfig from '../server/utils/getConfig';

interface ServerConfig {
    MICROSERVICES_URLS?: Record<ServiceName, string>;
}

let serverConfig: ServerConfig | null = null;

interface RequestConfig {
    method?: string;
    url: string;
    headers?: Record<string, string>;
    data?: any;
    params?: any;
    timeout?: number;
    responseType?: string;
    skipAuth?: boolean;
}

interface S2SAuthResult {
    config: RequestConfig;
    isS2SApplied: boolean;
}

const applyS2SAuth = async (
    serviceName: ServiceName,
    endpoint: string,
    config: RequestConfig
): Promise<S2SAuthResult> => {
    const endpointKey = `${serviceName}:${endpoint}`;
    const requestMethod = config.method?.toUpperCase();
    if (!getIsS2SEndpointAllowed(serviceName, endpoint, requestMethod)) {
        throw new Error(`S2S auth not allowed for endpoint: ${endpointKey}`);
    }

    if (isServer) {
        const s2sToken = await s2sTokenManager.getAccessToken();
        return {
            config: {
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: `Bearer ${s2sToken}`
                }
            },
            isS2SApplied: true
        };
    }

    const s2sKey = getS2SEndpointKey(serviceName, endpoint, requestMethod);
    if (s2sKey) {
        const endpointConfig = S2S_AUTH_ENDPOINTS[s2sKey];
        const {service} = endpointConfig;
        const isServerOnly = 'isServerOnly' in endpointConfig && endpointConfig.isServerOnly === true;
        if (isServerOnly) {
            throw new Error(`S2S endpoint ${s2sKey} is server-only and cannot be called from the browser`);
        }
        return {
            config: {
                ...config,
                url: `/s2s/${service.toLowerCase()}/${endpoint}`
            },
            isS2SApplied: true
        };
    }

    return {config, isS2SApplied: false};
};

const getServerConfig = async (): Promise<ServerConfig | null> => {
    if (!serverConfig && isServer) {
        try {
            const configResult = await getConfig();
            serverConfig = configResult?.default || configResult;
        } catch (error) {
            console.error('Failed to load server config:', error);
        }
    }
    return serverConfig;
};

export const getApiUrl = async (
    serviceName: ServiceName,
    endpoint: string
): Promise<string> => {
    if (isServer) {
        const config = await getServerConfig();
        if (config?.MICROSERVICES_URLS?.[serviceName]) {
            const baseUrl = config.MICROSERVICES_URLS[serviceName];
            const finalUrl = `${baseUrl}/api/${endpoint}`;

            return finalUrl;
        }
        throw new Error(`Microservice URL not found for service: ${serviceName}`);
    } else {
        const proxyPrefix = PROXY_PREFIXES[serviceName];
        if (!proxyPrefix) {
            throw new Error(`Proxy prefix not found for service: ${serviceName}`);
        }
        const finalUrl = `${proxyPrefix}/${endpoint}`;
        return finalUrl;
    }
};

export const createApiCall = <T = any>(
    serviceName: ServiceName,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => async (options: ApiCallOptions = {}): Promise<ApiResponse<T>> => {
        const {data, params, accessToken, useS2SAuth, skipAuth, headers, timeout, responseType} = options;

        const url = await getApiUrl(serviceName, endpoint);

        const config: any = {
            method: method.toLowerCase(),
            url,
            skipAuth
        };

        if (useS2SAuth) {
            const s2sResult = await applyS2SAuth(serviceName, endpoint, config);
            Object.assign(config, s2sResult.config);
        } else if (accessToken) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${accessToken}`
            };
        }

        if (headers) {
            config.headers = {
                ...config.headers,
                ...headers
            };
        }

        /** Explicitly compared to undefined so a caller can pass 0 and switch the timeout off. */
        if (timeout !== undefined) {
            config.timeout = timeout;
        }

        const isBinaryResponse = responseType === 'blob' || responseType === 'arraybuffer';

        if (responseType) {
            config.responseType = responseType;

            if (isBinaryResponse && !config.headers?.Accept) {
                config.headers = {
                    ...config.headers,
                    Accept: 'application/octet-stream'
                };
            }
        }

        if (data !== undefined) {
            config.data = data;
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                config.headers = {...config.headers};
                delete config.headers['Content-Type'];
                config.maxBodyLength = Infinity;
                config.maxContentLength = Infinity;
            }
        }

        if (params !== undefined) {
            config.params = params;
        }

        const response = await httpClient(config);

        if (isBinaryResponse) {
            return {data: response.data, status: response.status, headers: response.headers} as ApiResponse<T>;
        }

        return response.data;
    };

export const createFileUploadApiCall = <T = any>(
    serviceName: ServiceName,
    endpoint: string
) => async (
        file: File | Blob,
        options: ApiCallOptions = {}
    ): Promise<ApiResponse<T>> => {
        const {accessToken, useS2SAuth} = options;

        const url = await getApiUrl(serviceName, endpoint);

        const formData = new FormData();
        formData.append('file', file);

        const config: any = {
            method: 'post',
            url,
            data: formData,
            headers: {}
        };

        if (useS2SAuth) {
            const s2sResult = await applyS2SAuth(serviceName, endpoint, config);
            Object.assign(config, s2sResult.config);
        } else if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        if (options.headers) {
            config.headers = {
                ...config.headers,
                ...options.headers
            };
        }

        if (options.timeout) {
            config.timeout = options.timeout;
        }

        const response = await httpClient(config);
        return response.data;
    };

/**
 * Recursively fetches all pages from a paginated API endpoint
 * @param fetchFunction - Function that returns a PaginatedResponse
 * @param params - Base parameters for the API call
 * @param options - API call options
 * @returns Promise with all data combined from all pages
 */
export const fetchAllPages = async <T, P extends {page?: number; per_page?: number}>(
    fetchFunction: (params: P, options?: ApiCallOptions) => Promise<ApiResponse<PaginatedResponse<T>>>,
    params: P = {} as P,
    options: ApiCallOptions = {}
): Promise<T[]> => {
    const allData: T[] = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
        const response = await fetchFunction(
            {
                ...params,
                page: currentPage,
                per_page: params.per_page || 50 // Default page size
            },
            options
        );

        const responseData = response.data;
        const paginatedData = (responseData?.data && Array.isArray(responseData.data))
            ? responseData
            : (response as any);
        const perPage = params.per_page || 50;

        if (paginatedData?.data && Array.isArray(paginatedData.data) && paginatedData.meta) {
            allData.push(...paginatedData.data);
            const lastPage = paginatedData.meta.last_page
                ?? paginatedData.meta.total_pages
                ?? (paginatedData.meta.total ? Math.ceil(paginatedData.meta.total / perPage) : undefined);
            hasMorePages = lastPage !== undefined
                ? currentPage < lastPage
                : paginatedData.data.length >= perPage;
            currentPage += 1;
        } else if (Array.isArray(responseData)) {
            allData.push(...responseData);
            hasMorePages = false;
        } else {
            hasMorePages = false;
        }
    }

    return allData;
};
